import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check, Search } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Recursively flatten React children (or nested arrays / elements) to a
 * plain string. Fixes the "[object Object]" rendering that used to happen
 * when someone wrote `<option>{code} — {name}</option>` — the children
 * become an array of primitives, but `String([...])` on any array whose
 * elements aren't primitives collapses to "[object Object]".
 */
const childrenToText = (node) => {
  if (node == null || node === false) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(childrenToText).join("");
  if (React.isValidElement(node)) return childrenToText(node.props?.children);
  // Fall-through: unknown object → last-resort — never `[object Object]`.
  try { return String(node); } catch { return ""; }
};

/**
 * Coerce an unknown option-prop entry into `{value, label, disabled?, icon?, description?}`.
 * Accepts:
 *   {value,label}                 — canonical
 *   [value, label]                — tuple (e.g. US_STATES)
 *   {id, name} / {code, name}     — common backend shapes
 *   "someString"                  — plain string (value === label)
 *   primitive number              — value === label
 */
const normalizeOption = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string" || typeof raw === "number") {
    return { value: String(raw), label: String(raw) };
  }
  if (Array.isArray(raw)) {
    const [v, l] = raw;
    return { value: String(v ?? ""), label: String(l ?? v ?? "") };
  }
  if (typeof raw === "object") {
    const value = raw.value ?? raw.id ?? raw.code ?? raw.key;
    const label =
      raw.label ??
      raw.name ??
      raw.title ??
      raw.text ??
      (value != null ? String(value) : "");
    return {
      value: value != null ? String(value) : "",
      label: childrenToText(label),
      disabled: !!raw.disabled,
      icon: raw.icon,
      description: raw.description,
    };
  }
  return null;
};

/**
 * JeaSelect — dark-themed custom select.
 *
 * Two ways to pass options:
 *  1) `options={[{ value, label, disabled?, icon?, description? }]}` — preferred
 *  2) Nested <option> children (legacy) — auto-converted to options[]
 *
 * Props:
 *  - value: currently selected value (or "" for placeholder)
 *  - onChange: (value) => void  OR  (e) => void  (legacy — receives {target:{value}})
 *  - placeholder: displayed when no value
 *  - searchable: bool (defaults to true when options.length > 10)
 *  - disabled: bool
 *  - fullWidth: bool (default true)
 *  - id, testid, ariaLabel: forwarded
 */
const JeaSelect = React.forwardRef((props, ref) => {
  const {
    options: optionsProp,
    children,
    value,
    onChange,
    placeholder = "Select…",
    searchable,
    disabled = false,
    testid,
    "data-testid": dataTestid,
    fullWidth = true,
    className,
    id,
    ariaLabel,
    ...rest
  } = props;

  const tid = testid || dataTestid;

  // Normalize children → options for backward compatibility.
  // We also accept `options` prop shapes that aren't strictly {value,label} —
  // e.g. arrays of tuples `[value, label]` or objects with `id`/`name` — so
  // that dropdown labels never fall through to `[object Object]`.
  const options = useMemo(() => {
    if (optionsProp) return optionsProp.map(normalizeOption).filter(Boolean);
    const collected = [];
    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child)) return;
      if (child.type === "option") {
        const v = child.props.value;
        if (v === "" && child.props.hidden) return; // hidden placeholder
        collected.push({
          value: v,
          label: childrenToText(child.props.children) || String(v ?? ""),
          disabled: !!child.props.disabled,
        });
      }
    });
    return collected;
  }, [optionsProp, children]);

  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const searchInputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1);
  const [rect, setRect] = useState(null);

  const showSearch = searchable ?? options.length > 10;

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) =>
      (o.label || "").toLowerCase().includes(q) ||
      (o.description || "").toLowerCase().includes(q) ||
      String(o.value || "").toLowerCase().includes(q)
    );
  }, [options, query]);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)),
    [options, value]
  );

  useEffect(() => {
    if (!open) { setQuery(""); setActiveIdx(-1); return; }
    if (showSearch) setTimeout(() => searchInputRef.current?.focus(), 0);
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    const onClickOutside = (e) => {
      if (
        !triggerRef.current?.contains(e.target) &&
        !popoverRef.current?.contains(e.target)
      ) setOpen(false);
    };
    const onScroll = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (r) setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    document.addEventListener("mousedown", onClickOutside);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, showSearch]);

  const emit = (val) => {
    if (!onChange) return;
    // Prefer (value) signature; fall back to synthetic event for legacy consumers.
    try { onChange(val); }
    catch { onChange({ target: { value: val } }); }
  };

  const select = (opt) => {
    if (opt.disabled) return;
    emit(opt.value);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open && (e.key === "Enter" || e.key === "ArrowDown" || e.key === " ")) {
      e.preventDefault(); setOpen(true); return;
    }
    if (!open) return;
    if (e.key === "Escape") { setOpen(false); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0 && filtered[activeIdx]) select(filtered[activeIdx]);
    }
  };

  return (
    <>
      <button
        ref={(el) => { triggerRef.current = el; if (ref) { if (typeof ref === "function") ref(el); else ref.current = el; } }}
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        data-testid={tid}
        className={cn(
          "inline-flex items-center justify-between gap-2 h-11 px-4 rounded-[8px] bg-bg-primary border border-border-default text-white text-[14px] font-medium transition-all",
          "hover:border-border-hover focus:outline-none focus:border-accent-pink focus:shadow-pink-glow",
          disabled && "opacity-50 cursor-not-allowed",
          fullWidth && "w-full",
          className,
        )}
        {...rest}
      >
        <span className={cn("truncate text-left", !selected && "text-text-secondary")}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown
          size={16}
          className={cn("text-text-secondary shrink-0 transition-transform duration-150", open && "rotate-180")}
        />
      </button>

      {rect && ReactDOM.createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              ref={popoverRef}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15 }}
              style={{
                position: "fixed",
                top: rect.top,
                left: rect.left,
                width: rect.width,
                zIndex: 200,
              }}
              className="rounded-[8px] bg-bg-secondary border border-border-default shadow-elevated max-h-[320px] overflow-hidden flex flex-col"
              data-testid={tid ? `${tid}-popover` : undefined}
              role="listbox"
            >
              {showSearch && (
                <div className="p-2 border-b border-border-default">
                  <div className="relative">
                    <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input
                      ref={searchInputRef}
                      value={query}
                      onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
                      onKeyDown={onKeyDown}
                      placeholder="Search…"
                      className="w-full h-9 pl-8 pr-3 rounded-[6px] bg-bg-primary border border-border-default text-white text-[13px] focus:outline-none focus:border-accent-pink"
                      data-testid={tid ? `${tid}-search` : undefined}
                    />
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-y-auto py-1">
                {filtered.length === 0 ? (
                  <div className="px-4 py-3 text-text-muted text-[13px]">No matches.</div>
                ) : (
                  filtered.map((opt, i) => {
                    const isSelected = String(opt.value) === String(value);
                    const isActive = i === activeIdx;
                    return (
                      <button
                        key={String(opt.value)}
                        type="button"
                        disabled={opt.disabled}
                        onClick={() => select(opt)}
                        onMouseEnter={() => setActiveIdx(i)}
                        role="option"
                        aria-selected={isSelected}
                        data-testid={tid ? `${tid}-option-${opt.value}` : undefined}
                        className={cn(
                          "w-full flex items-center gap-2 px-4 py-2.5 text-left text-[14px] transition-colors",
                          opt.disabled
                            ? "text-text-muted cursor-not-allowed"
                            : isSelected
                              ? "bg-accent-pink/10 text-accent-pink"
                              : isActive
                                ? "bg-bg-tertiary text-white"
                                : "text-white hover:bg-bg-tertiary",
                        )}
                      >
                        {opt.icon}
                        <span className="flex-1 truncate">
                          {opt.label}
                          {opt.description && (
                            <span className="block text-text-muted text-[11px] mt-0.5">{opt.description}</span>
                          )}
                        </span>
                        {isSelected && <Check size={14} className="text-accent-pink shrink-0" />}
                      </button>
                    );
                  })
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
});
JeaSelect.displayName = "JeaSelect";

export const US_STATES = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"],
  ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"],
  ["DC", "District of Columbia"], ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"],
  ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"],
  ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"],
  ["MD", "Maryland"], ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"],
  ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"],
  ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"],
  ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"],
  ["OK", "Oklahoma"], ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"],
  ["SC", "South Carolina"], ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"],
  ["UT", "Utah"], ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"],
  ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
];

export default JeaSelect;
